"use client";

import { X } from "lucide-react";
import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { moveItem, SortableList } from "@/components/SortableList";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { usePdfSummaries } from "@/hooks/usePdfSummaries";
import { useToolFiles } from "@/hooks/useToolFiles";
import { formatBytes, outputName } from "@/lib/files";
import { getTool } from "@/lib/tools";
import { mergeFiles } from "@/workers/pdf-client";

const tool = getTool("merge-pdf")!;

const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

/** FR-10: up to 20 PDFs, first-page previews, drag to reorder, merged in a worker. */
export function MergeTool() {
  const { files, setFiles, notices } = useToolFiles(tool);
  const summaries = usePdfSummaries(files);
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const unreadable = files
    .map((f) => {
      const s = summaries.get(f);
      return s && "error" in s ? `${f.name}: ${s.error}` : null;
    })
    .filter((m): m is string => m !== null);
  const totalPages = files.reduce((sum, f) => {
    const s = summaries.get(f);
    return sum + (s && "pages" in s ? s.pages : 0);
  }, 0);

  function change(next: File[]) {
    setFiles(next);
    setResults(null);
    setError("");
  }

  async function merge() {
    setBusy(true);
    setError("");
    try {
      const blob = await mergeFiles(files);
      setResults([
        {
          blob,
          name: outputName(files[0].name, "merged", "pdf"),
          detail: `${totalPages} pages`,
        },
      ]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={change}
      notices={notices}
      results={results}
      onStartOver={() => change([])}
      fileList={
        <SortableList
          label="PDFs to merge, in order"
          items={files}
          itemKey={fileKey}
          itemLabel={(f) => f.name}
          onReorder={(from, to) => change(moveItem(files, from, to))}
          renderItem={(file, i, { handle, moveButtons }) => {
            const s = summaries.get(file);
            return (
              <div className="flex items-center gap-2 rounded-lg border bg-card p-1.5 sm:gap-3">
                {handle}
                <span className="w-5 shrink-0 text-center font-heading text-lg text-muted-foreground">
                  {i + 1}
                </span>
                <span className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-white">
                  {s && "thumb" in s ? (
                    <BlobImage
                      blob={s.thumb}
                      alt=""
                      className="max-h-full max-w-full"
                    />
                  ) : (
                    <span className="size-full animate-pulse bg-sand" />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {s && "pages" in s
                      ? `${s.pages} ${s.pages === 1 ? "page" : "pages"} · `
                      : ""}
                    {formatBytes(file.size)}
                  </span>
                  {s && "error" in s && (
                    <span className="text-sm font-medium text-destructive">
                      {s.error}
                    </span>
                  )}
                </span>
                <span className="hidden sm:flex">{moveButtons}</span>
                <button
                  type="button"
                  onClick={() => change(files.filter((_, j) => j !== i))}
                  aria-label={`Remove ${file.name}`}
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-sand hover:text-ink"
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </div>
            );
          }}
        />
      }
    >
      <section
        aria-label="Merge"
        className="sticky bottom-0 flex flex-col gap-3 rounded-[16px] border bg-card p-4 sm:p-5"
      >
        {files.length < 2 ? (
          <p className="text-ink">Add at least one more PDF to merge.</p>
        ) : (
          <p className="text-ink">
            {files.length} files
            {totalPages ? ` · ${totalPages} pages in total` : ""}. Drag to
            change the order.
          </p>
        )}
        {(unreadable.length > 0 || error) && (
          <ul role="alert" className="text-sm font-medium text-destructive">
            {unreadable.length > 0 && (
              <li>
                {unreadable.length === 1
                  ? "Remove the file marked in red to merge."
                  : "Remove the files marked in red to merge."}
              </li>
            )}
            {error && <li>{error}</li>}
          </ul>
        )}
        {busy ? (
          <ProgressBar label="Merging your PDFs…" />
        ) : (
          <button
            type="button"
            onClick={merge}
            disabled={files.length < 2 || unreadable.length > 0}
            className={primaryButtonClass}
          >
            Merge {files.length > 1 ? `${files.length} PDFs` : "PDFs"}
          </button>
        )}
        <p className="text-center text-[13px] text-muted-foreground">
          Nothing is uploaded
        </p>
      </section>
    </ToolShell>
  );
}
